import React, { useState, useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import './index.css'
import { ProjectSwitcher } from './components/ProjectSwitcher'
import { ChatInterface } from './components/ChatInterface'
import { FileUpload } from './components/FileUpload'
import { ProposalModal } from './components/ProposalModal'
import { StageProgressList, StageProgress } from './components/StageProgressList'
import { ErrorAlert } from './components/ErrorAlert'
import { ProtectedRoute } from './components/ProtectedRoute'
import { NewProjectDialog } from './components/NewProjectDialog'
import { ProjectSettingsDialog } from './components/ProjectSettingsDialog'
import { LoginPage } from './pages/LoginPage'
import { AdminPage } from './pages/AdminPage'
import { AcceptInvitePage } from './pages/AcceptInvitePage'
import { Card, CardContent } from './components/ui/card'
import { useConversation } from './hooks/useConversation'
import { useAuth } from './contexts/AuthContext'
import { LogOut, User, Shield, Settings2 } from 'lucide-react'
import { conversationalApi } from './services/conversationalApi'

function MainApp() {
  const { user, logout, effectiveUserRole, effectiveRole, setEffectiveRole, clearEffectiveRole } = useAuth()
  const [activeProjectId, setActiveProjectId] = useState<string>('')
  const [activeProject, setActiveProject] = useState<{ id: string; name: string } | null>(null)
  const [showNewProjectDialog, setShowNewProjectDialog] = useState(false)
  const [refreshProjectList, setRefreshProjectList] = useState(0)
  const [showProposalModal, setShowProposalModal] = useState(false)
  const [showProjectSettings, setShowProjectSettings] = useState(false)

  // Use the conversational hook
  const {
    messages,
    currentMode,
    isLoading,
    isConnected,
    workflowProgress,
    error,
    nextActions,
    sendMessage,
    uploadFile,
    clearError,
    getProposal,
  } = useConversation(activeProjectId)

  const [proposal, setProposal] = useState<string>('')

  // Auto-load proposal when workflow completes
  useEffect(() => {
    const loadProposalWhenComplete = async () => {
      if (workflowProgress?.status === 'completed' && !proposal) {
        console.log('🔥 Workflow completed, auto-loading proposal...')
        const proposalContent = await getProposal()
        if (proposalContent) {
          console.log('🔥 Proposal auto-loaded successfully')
          setProposal(proposalContent)
        } else {
          console.log('🔥 No proposal content returned from auto-load')
        }
      }
    }

    loadProposalWhenComplete()
  }, [workflowProgress?.status, proposal, getProposal])

  const handleSendMessage = async (message: string) => {
    // Check if a project is selected
    if (!activeProjectId) {
      alert('⚠️ Please create or select a project first.\n\nClick "New Project" to get started.')
      return
    }

    const response = await sendMessage(message)

    // If this was a proposal refinement, refresh the proposal
    if (response && response.mode === 'proposal_refinement') {
      console.log('🔄 Proposal refinement detected, refreshing proposal...')

      // Small delay to ensure database transaction is committed
      await new Promise(resolve => setTimeout(resolve, 500))

      // Backend now saves immediately, so fetch after brief delay
      const proposalContent = await getProposal()
      if (proposalContent) {
        console.log('🔄 Proposal refreshed after refinement:', proposalContent.length, 'chars')
        console.log('🔄 First 200 chars:', proposalContent.substring(0, 200))
        setProposal(proposalContent)
        console.log('🔄 State updated, proposal length in state should be:', proposalContent.length)
      } else {
        console.warn('🔄 No proposal content returned after refinement')
      }
    }
  }

  const handleFileUpload = async (files: File[]) => {
    // Check if a project is selected
    if (!activeProjectId) {
      alert('⚠️ Please create or select a project first before uploading files.\n\nClick "New Project" to get started.')
      return
    }

    console.log('🔥 App handleFileUpload called with:', files.length, 'file(s), calling uploadFile...')
    await uploadFile(files)
    console.log('🔥 App handleFileUpload completed for', files.length, 'file(s)')

    // Check if we should load the proposal after upload
    if (workflowProgress?.status === 'completed') {
      console.log('🔥 Workflow completed, loading proposal...')
      const proposalContent = await getProposal()
      if (proposalContent) {
        console.log('🔥 Proposal loaded successfully')
        setProposal(proposalContent)
      } else {
        console.log('🔥 No proposal content returned')
      }
    }
  }

  const handleProjectChange = async (projectId: string) => {
    console.log('📂 Switching to project:', projectId)

    if (!projectId) {
      setActiveProjectId('')
      setProposal('')
      return
    }

    setActiveProjectId(projectId)
    setProposal('') // Clear current proposal first

    // Load proposal for the selected project
    // Note: useConversation hook will automatically reload conversation history
    // when activeProjectId changes (see useEffect in useConversation.ts line 177-207)
    try {
      // Use conversationalApi directly with the new projectId
      // (can't use getProposal from hook as it still has the old projectId in closure)
      const proposalContent = await conversationalApi.getProposal(projectId)
      if (proposalContent) {
        console.log('📂 Loaded proposal for project:', projectId)
        setProposal(proposalContent)
      } else {
        console.log('📂 No proposal found for project:', projectId)
      }
    } catch (error) {
      console.log('📂 No proposal available for this project:', error)
      // This is expected for projects without proposals yet
    }
  }

  const handleLogout = async () => {
    await logout()
    window.location.href = '/login'
  }

  const stageOrder = [
    'project_intake',
    'solution_architecture',
    'scope_decomposition',
    'effort_estimation',
    'proposal_generation',
    'success_assessment',
    'proposal_review',
  ] as const

  const stageStatusFor = (stageKey: typeof stageOrder[number]): StageProgress['status'] => {
    if (!workflowProgress) return 'pending'
    if (workflowProgress.status === 'completed') return 'completed'

    if (workflowProgress.status === 'failed') {
      if (workflowProgress.currentStage === stageKey) return 'failed'
      const currentIdx = workflowProgress.currentStage ? stageOrder.indexOf(workflowProgress.currentStage as any) : -1
      const idx = stageOrder.indexOf(stageKey)
      return currentIdx >= 0 && idx < currentIdx ? 'completed' : 'pending'
    }

    const completed = workflowProgress.stagesCompleted || 0
    const idx = stageOrder.indexOf(stageKey)
    if (completed > idx) return 'completed'
    if (workflowProgress.currentStage === stageKey) return 'running'
    return 'pending'
  }

  // Convert workflow progress to stages format
  const stages: StageProgress[] = [
    {
      id: 'intake',
      name: 'Project Intake',
      description: 'Analyzing requirements',
      status: stageStatusFor('project_intake'),
    },
    {
      id: 'architecture',
      name: 'Solution Architecture',
      description: 'Designing system architecture',
      status: stageStatusFor('solution_architecture'),
    },
    {
      id: 'scope',
      name: 'Scope Decomposition',
      description: 'Breaking down work components',
      status: stageStatusFor('scope_decomposition'),
    },
    {
      id: 'estimation',
      name: 'Effort Estimation',
      description: 'Calculating resource requirements',
      status: stageStatusFor('effort_estimation'),
    },
    {
      id: 'proposal',
      name: 'Proposal Generation',
      description: 'Creating deliverable proposal',
      status: stageStatusFor('proposal_generation'),
    },
    {
      id: 'assessment',
      name: 'Success Assessment',
      description: 'Evaluating project viability',
      status: stageStatusFor('success_assessment'),
    },
    {
      id: 'review',
      name: 'Proposal Review',
      description: 'Final review and recommendations',
      status: stageStatusFor('proposal_review'),
    },
  ]

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(to bottom right, var(--cream), var(--sand))' }}>
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 style={{
                fontFamily: 'Crimson Text, Georgia, serif',
                fontSize: '2.5rem',
                fontWeight: 600,
                color: 'var(--charcoal)',
                letterSpacing: '-0.02em',
              }}>
                Adya
              </h1>
              <p style={{
                fontFamily: 'DM Sans, sans-serif',
                color: 'var(--charcoal-light)',
                marginTop: '0.25rem',
              }}>
                Conversational AI Proposal System
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full" style={{
                background: user ? 'var(--cream)' : 'var(--sand)',
                color: 'var(--charcoal)',
                fontSize: '0.875rem',
              }}>
                <User className="w-4 h-4" />
                <span>{user?.full_name || 'Guest'}</span>
              </div>

              {user?.role === 'admin' && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-md" style={{
                  background: 'rgba(0, 0, 0, 0.04)',
                  color: 'var(--charcoal-light)',
                  border: '1px solid var(--sand)',
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: '0.875rem',
                }}>
                  <span style={{ whiteSpace: 'nowrap' }}>View as</span>
                  <select
                    value={effectiveUserRole || 'admin'}
                    onChange={(e) => {
                      const next = e.target.value
                      if (next === 'admin') {
                        clearEffectiveRole()
                      } else {
                        setEffectiveRole(next)
                      }
                    }}
                    className="px-2 py-1 rounded-md border"
                    style={{
                      borderColor: 'var(--sand)',
                      background: 'var(--warm-white)',
                      color: 'var(--charcoal)',
                      fontFamily: 'DM Sans, sans-serif',
                      fontSize: '0.875rem',
                    }}
                  >
                    <option value="admin">admin</option>
                    <option value="manager">manager</option>
                    <option value="member">member</option>
                  </select>
                  {effectiveRole && (
                    <button
                      onClick={clearEffectiveRole}
                      className="px-2 py-1 rounded-md"
                      style={{
                        background: 'rgba(200, 88, 53, 0.08)',
                        color: 'var(--terracotta)',
                        border: '1px solid rgba(200, 88, 53, 0.25)',
                      }}
                    >
                      Reset
                    </button>
                  )}
                </div>
              )}
              {user && (
                <button
                  onClick={() => {
                    window.location.href = '/admin'
                  }}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-md transition-colors"
                  style={{
                    background: 'rgba(200, 88, 53, 0.08)',
                    color: 'var(--terracotta)',
                    border: '1px solid rgba(200, 88, 53, 0.25)',
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: '0.875rem',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(200, 88, 53, 0.14)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(200, 88, 53, 0.08)'
                  }}
                >
                  <Shield className="w-4 h-4" />
                  {effectiveUserRole === 'admin' ? 'Admin' : 'Prompts'}
                </button>
              )}

              {activeProjectId && (
                <button
                  onClick={() => setShowProjectSettings(true)}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-md transition-colors"
                  style={{
                    background: 'rgba(0, 0, 0, 0.04)',
                    color: 'var(--charcoal-light)',
                    border: '1px solid var(--sand)',
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: '0.875rem',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(200, 88, 53, 0.05)'
                    e.currentTarget.style.borderColor = 'var(--terracotta)'
                    e.currentTarget.style.color = 'var(--terracotta)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(0, 0, 0, 0.04)'
                    e.currentTarget.style.borderColor = 'var(--sand)'
                    e.currentTarget.style.color = 'var(--charcoal-light)'
                  }}
                >
                  <Settings2 className="w-4 h-4" />
                  Project Settings
                </button>
              )}
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm`} style={{
                background: isConnected ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                color: isConnected ? '#15803d' : '#991b1b',
              }}>
                <div className="w-2 h-2 rounded-full" style={{
                  background: isConnected ? '#22c55e' : '#ef4444',
                }} />
                {isConnected ? 'Connected' : 'Disconnected'}
              </div>
              {currentMode !== 'general_chat' && (
                <div className="px-3 py-1.5 rounded-full text-sm" style={{
                  background: 'rgba(200, 88, 53, 0.1)',
                  color: 'var(--terracotta-dark)',
                }}>
                  Mode: {currentMode.replace('_', ' ')}
                </div>
              )}
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-3 py-1.5 rounded-md transition-colors"
                style={{
                  background: 'transparent',
                  color: 'var(--charcoal-light)',
                  border: '1px solid var(--sand)',
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: '0.875rem',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(200, 88, 53, 0.05)';
                  e.currentTarget.style.borderColor = 'var(--terracotta)';
                  e.currentTarget.style.color = 'var(--terracotta)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = 'var(--sand)';
                  e.currentTarget.style.color = 'var(--charcoal-light)';
                }}
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>

          <ProjectSwitcher
            key={refreshProjectList}
            activeProjectId={activeProjectId}
            onProjectChange={handleProjectChange}
            onProjectSelect={(project) => setActiveProject(project)}
            onNewProject={() => setShowNewProjectDialog(true)}
          />
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6">
            <ErrorAlert
              error={error}
              onDismiss={clearError}
            />
          </div>
        )}

        {/* Main Content */}
        <div className="grid grid-cols-1 gap-6">
          {/* Workflow Progress Bar (only show when workflow is active) */}
          {workflowProgress && workflowProgress.status !== 'completed' && (
            <div className="sticky top-4 z-20">
              <StageProgressList
                stages={stages}
                activeAgentName={workflowProgress.agentName}
                activeStage={workflowProgress.currentStage}
                activeMessage={workflowProgress.message}
                activeStatus={workflowProgress.status}
                startedAt={workflowProgress.startedAt}
                variant="bar"
              />
            </div>
          )}

          {/* Chat Interface */}
          <div className="mb-6">
            <Card>
              <CardContent className="p-6">
                <ChatInterface
                  messages={messages}
                  onSendMessage={handleSendMessage}
                  isProcessing={isLoading}
                  suggestions={nextActions}
                />
              </CardContent>
            </Card>
          </div>

          {/* File Upload */}
          <div className="mb-6">
            <FileUpload
              onFileUpload={handleFileUpload}
              projectId={activeProjectId}
              disabled={isLoading}
            />
          </div>

          {/* View Proposal Button (when proposal is available) */}
          {proposal && (
            <div className="mb-6">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold mb-1" style={{ color: 'var(--charcoal)' }}>
                        Proposal Ready
                      </h3>
                      <p className="text-sm" style={{ color: 'var(--charcoal-light)' }}>
                        Your project proposal has been generated and is ready to view
                      </p>
                    </div>
                    <button
                      onClick={() => setShowProposalModal(true)}
                      className="px-6 py-3 rounded-md font-medium transition-colors"
                      style={{
                        background: 'var(--terracotta)',
                        color: 'white',
                        fontFamily: 'DM Sans, sans-serif',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--terracotta-dark)';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(200, 88, 53, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'var(--terracotta)';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      View Proposal
                    </button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>

      {/* New Project Dialog */}
      <NewProjectDialog
        isOpen={showNewProjectDialog}
        onClose={() => setShowNewProjectDialog(false)}
        onProjectCreated={(projectId) => {
          setActiveProjectId(projectId)
          setProposal('')
          setShowNewProjectDialog(false)
          setRefreshProjectList(prev => prev + 1) // Trigger project list refresh
        }}
      />

      {/* Project Settings Dialog */}
      {activeProjectId && (
        <ProjectSettingsDialog
          isOpen={showProjectSettings}
          onClose={() => setShowProjectSettings(false)}
          projectId={activeProjectId}
        />
      )}

      {/* Proposal Modal */}
      {proposal && (
        <ProposalModal
          isOpen={showProposalModal}
          onClose={() => setShowProposalModal(false)}
          proposal={proposal}
          projectId={activeProjectId}
          projectName={activeProject?.name}
          onProposalUpdated={async (mergedMarkdown) => {
            setProposal(mergedMarkdown)

            await new Promise(resolve => setTimeout(resolve, 300))
            const refreshed = await getProposal()
            if (refreshed) {
              setProposal(refreshed)
            }
          }}
        />
      )}
    </div>
  )
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <MainApp />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
